import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { useSqlDashboardStore } from "@/lib/store";
import { useMemo } from "react";
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";

export function ResultsTable() {
  const { queryResults, error, isExecuting } = useSqlDashboardStore();

  const data = useMemo(() => queryResults?.rows || [], [queryResults]);

  const columns = useMemo(() => {
    if (!data.length) return [];
    const helper = createColumnHelper<any>();
    return Object.keys(data[0]).map((key) =>
      helper.accessor(key, {
        header: key,
        cell: (info) => {
          const val = info.getValue();
          if (val === null)
            return (
              <span className="text-muted-foreground/40 italic">null</span>
            );
          if (typeof val === "object" || Array.isArray(val))
            return (
              <span className="text-primary font-mono">
                {JSON.stringify(val)}
              </span>
            );
          return String(val);
        },
      })
    );
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleExport = (format: "csv" | "json") => {
    if (!data.length) return;

    let content = "";
    let type = "";
    let filename = "query_results";

    if (format === "json") {
      content = JSON.stringify(data, null, 2);
      type = "application/json";
      filename += ".json";
    } else {
      const headers = Object.keys(data[0]);
      content =
        headers.join(",") +
        "\n" +
        data
          .map((row) =>
            headers
              .map((h) => {
                const val = row[h];
                if (val === null || val === undefined) return "";
                const str = String(val);
                return str.includes(",") || str.includes('"') || str.includes("\n")
                  ? `"${str.replace(/"/g, '""')}"`
                  : str;
              })
              .join(",")
          )
          .join("\n");
      type = "text/csv";
      filename += ".csv";
    }

    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} rows as ${format.toUpperCase()}`);
  };

  if (error) {
    return (
      <div className="h-full p-6 flex flex-col items-center justify-center bg-destructive/5">
        <ExclamationTriangleIcon className="w-8 h-8 text-destructive/70 mb-3" />
        <h3 className="text-sm font-semibold text-destructive mb-2">
          Query Failed
        </h3>
        <p className="font-mono text-xs bg-card p-3 rounded-md border border-destructive/20 max-w-lg text-center text-foreground/70">
          {error}
        </p>
      </div>
    );
  }

  if (isExecuting) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-8 h-8">
            <div className="absolute inset-0 border-2 border-primary/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
          <span className="text-xs font-medium text-muted-foreground animate-pulse">
            Running query...
          </span>
        </div>
      </div>
    );
  }

  if (!queryResults) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground/50 p-8 text-center">
        <ArrowDownTrayIcon className="w-8 h-8 mb-3 opacity-40" />
        <h3 className="text-sm font-medium text-foreground/60 mb-1">
          Ready to Query
        </h3>
        <p className="text-xs max-w-xs text-muted-foreground/60">
          Write a SQL query above and press Run to see results
        </p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-full p-4 flex flex-col">
        <div className="px-3 py-2 bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400 rounded-md text-xs font-medium flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
          Query executed successfully
          {queryResults.affectedRows !== undefined && (
            <span className="opacity-60 font-normal ml-auto">
              {queryResults.affectedRows} rows affected
            </span>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center text-muted-foreground/40 text-xs mt-4 border border-dashed border-border rounded-lg">
          No records returned
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-card">
      <div className="px-4 py-1.5 border-b border-border bg-muted/20 flex justify-between items-center shrink-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">
            {data.length} rows
          </span>
          {queryResults.affectedRows !== undefined && (
            <>
              <span className="w-px h-3 bg-border" />
              <span>{queryResults.affectedRows} affected</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => handleExport("csv")}
            className="text-xs flex items-center gap-1 hover:text-foreground hover:bg-accent px-2 py-0.5 rounded-md transition-colors text-muted-foreground"
          >
            <ArrowDownTrayIcon className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            className="text-xs flex items-center gap-1 hover:text-foreground hover:bg-accent px-2 py-0.5 rounded-md transition-colors text-muted-foreground"
          >
            <ArrowDownTrayIcon className="w-3 h-3" />
            JSON
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs text-left border-collapse min-w-max">
          <thead className="text-[11px] text-muted-foreground uppercase bg-muted/40 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 font-medium border-b border-border whitespace-nowrap bg-muted/50"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border/40">
            {table.getRowModel().rows.map((row, idx) => (
              <tr
                key={row.id}
                className={`hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? "bg-card" : "bg-muted/10"}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-2 whitespace-nowrap font-mono text-[11px] text-foreground/75 max-w-[400px] overflow-hidden text-ellipsis"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
